/** Pure text for resource prices: what the price is, where it came from and why it may not apply. */
import type { paths } from "./api-schema";

type PriceQuery = NonNullable<paths["/api/v1/projects/{project_id}/resource-prices"]["get"]["parameters"]["query"]>;

/** One page of the price lookup. The resource's own unit lets the server flag a price in another unit. */
export function priceQuery(query: string, offset: number, unit?: string): PriceQuery {
  return { query, offset, limit: 10, ...(unit ? { unit } : {}) };
}

/** Rubles read as «₽», as everywhere else in the estimate; other currencies keep their code. */
export function currencyLabel(currency = "") {
  return currency === "RUB" ? "₽" : currency;
}

export type PriceLevels = {
  price_basis?: "base" | "current" | "unspecified";
  base_price?: string | null;
  index?: string | null;
  current_price?: string | null;
  current_origin?: "source" | "computed" | null;
  current_formula?: string;
  current_reason?: string;
  base_state?: string;
  current_state?: string;
  index_state?: string;
  base_source_value?: string;
  current_source_value?: string;
  index_source_value?: string;
};
export type PriceCard = PriceLevels & {
  status: "current" | "stale" | "absent" | "unchecked";
  reason?: string;
  code?: string;
  unit_price?: string | null;
  price_unit?: string;
  currency?: string;
  source?: string;
  file?: string;
  sha256?: string;
  download_url?: string | null;
  catalog_name?: string;
  edition?: string;
  region?: string;
  period?: string;
  base_level?: string;
};
export type PriceRow = { label: string; value: string; note?: string };

function sourceMarkerNote(state: string | undefined, value: string | undefined) {
  if (!state || state === "numeric" || state === "explicit_zero") return undefined;
  const raw = value?.trim();
  return `в источнике: «${raw || "пусто"}»`;
}

/** Decimal strings are shown as written; a float round-trip could change a price. */
export function decimalText(value: string | null | undefined) {
  if (value == null || value === "") return "не указано";
  const [whole, fraction = ""] = value.split(".");
  const digits = fraction.replace(/0+$/, "");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return digits ? `${grouped},${digits}` : grouped;
}

export function priceText(value: string | null | undefined, currency = "", unit = "") {
  if (value == null || value === "") return "не указана";
  return [decimalText(value), currencyLabel(currency), unit && `/ ${unit}`].filter(Boolean).join(" ");
}

/** Базисная цена, индекс и текущая цена — три отдельные строки; индекс применяется один раз. */
export function levelRows(levels: PriceLevels, currency = "", unit = ""): PriceRow[] {
  const rows: PriceRow[] = [];
  if (levels.price_basis === "base" || levels.base_price != null)
    rows.push({ label: "Базисная цена", value: priceText(levels.base_price, currency, unit),
      note: sourceMarkerNote(levels.base_state, levels.base_source_value) });
  if (levels.price_basis !== "unspecified" || levels.index != null)
    rows.push({ label: "Индекс источника", value: levels.index == null ? "не указан" : decimalText(levels.index),
      note: sourceMarkerNote(levels.index_state, levels.index_source_value) });
  const sourceValue = levels.current_source_value?.trim();
  const sourceNote = sourceValue && levels.current_state && levels.current_state !== "numeric"
    ? `в источнике: «${sourceValue || "пусто"}»`
    : "";
  rows.push({
    label: "Текущая цена",
    value: levels.current_price == null ? "не рассчитана" : priceText(levels.current_price, currency, unit),
    note: levels.current_price == null
      ? levels.current_reason || "Причина не указана"
      : levels.current_origin === "computed"
      ? [`рассчитана: ${levels.current_formula || "базисная цена × индекс источника"}`, sourceNote].filter(Boolean).join(" · ")
        : ["из источника, повторно не индексируется", sourceNote].filter(Boolean).join(" · "),
  });
  return rows;
}

export function sourceRows(card: PriceCard): PriceRow[] {
  const rows: PriceRow[] = [];
  if (card.source) rows.push({ label: "Источник", value: card.source });
  if (card.file || card.sha256)
    rows.push({ label: "Файл версии цен", value: card.file || "исходник", note: card.sha256 ? `SHA-256 ${card.sha256}` : "" });
  if (card.catalog_name || card.edition)
    rows.push({ label: "База и редакция", value: [card.catalog_name, card.edition].filter(Boolean).join(" · ") });
  if (card.region) rows.push({ label: "Регион", value: card.region });
  if (card.base_level) rows.push({ label: "Базовый уровень источника", value: card.base_level });
  if (card.period) rows.push({ label: "Период справочника", value: card.period });
  return rows;
}

export const cardStatus: Record<PriceCard["status"], string> = {
  current: "Цена найдена и действует",
  stale: "Проверку цены нужно повторить",
  absent: "Цена в базе отсутствует",
  unchecked: "Цена не проверена",
};

/** One sentence under the status: a stale price is still shown, but never as applicable. */
export function cardSummary(card: PriceCard) {
  if (card.status === "current") return "";
  return card.reason || "Причина не указана";
}

/** Register line: the found price with its period and source, and the reason unless the note already carries it. */
export function registerPrice(line: { price_status: string; unit_price?: string | null; currency?: string; price_unit?: string;
  price_level?: string; price_source?: string; price_reason?: string; note?: string }) {
  const price = line.price_status === "found" && line.unit_price != null
    ? [priceText(line.unit_price, line.currency, line.price_unit), line.price_level, line.price_source].filter(Boolean).join(" · ")
    : "";
  const reason = line.price_reason && !(line.note || "").includes(line.price_reason) ? line.price_reason : "";
  return { price, reason };
}

export const availabilityText: Record<string, string> = {
  not_loaded: "цены ресурсов не загружены",
  partial: "цены загружены частично",
  ready: "цены загружены",
  unconfirmed: "цены загружены, база не подтверждена",
};

/** Why «Использовать цену» is inactive for this hit; '' when it can be used. */
export function useBlockedReason(hit: { blocked_reason?: string; unit_price?: string | null }, canEdit: boolean) {
  if (!canEdit) return "Изменение ресурсов недоступно: нет прав на правку или работа открыта только для чтения.";
  if (hit.blocked_reason) return hit.blocked_reason;
  return hit.unit_price == null ? "В источнике цена этого ресурса не указана." : "";
}

export type Readiness = { key: string; title: string; state: string; ready: boolean; detail?: string; found?: number | null; total?: number | null };

const stateText: Record<string, string> = {
  not_selected: "не выбрана",
  working_unconfirmed: "рабочий каталог, не подтверждена",
  confirmed: "подтверждено",
  not_loaded: "не загружены",
  loaded_not_selected: "загружены, версия не выбрана",
  selected: "версия выбрана",
  absent: "не заданы",
  draft: "не подтверждены",
  outdated: "устарели",
};

export function readinessLine(item: Readiness) {
  const counted = item.state === "selected" && item.total != null ? ` · цена у ${item.found ?? 0} из ${item.total}` : "";
  return { tone: item.ready ? "ready" : "pending", text: `${item.title}: ${stateText[item.state] ?? item.state}${counted}` };
}

export type ParameterRequest = { key: string; label: string; question: string; received: boolean; value?: string };

export function requestSummary(requests: ParameterRequest[]) {
  const open = requests.filter((r) => !r.received).length;
  return open ? `Получить у заказчика · ${open} из ${requests.length}` : "От заказчика получено всё необходимое";
}

/** Values of the chosen price version that differ from the form; offered, never applied silently. */
export function sourceSuggestion(metadata: Record<string, unknown> | undefined, parameters: { region: string; price_level: string }) {
  const region = typeof metadata?.region === "string" ? metadata.region.trim() : "";
  const period = typeof metadata?.price_level === "string" ? metadata.price_level.trim() : "";
  const patch: { region?: string; price_level?: string } = {};
  if (region && region !== parameters.region) patch.region = region;
  if (period && period !== parameters.price_level) patch.price_level = period;
  if (!patch.region && !patch.price_level) return null;
  const parts = [patch.region && `регион «${patch.region}»`, patch.price_level && `период «${patch.price_level}»`].filter(Boolean);
  return { patch, text: `В выбранной версии цен: ${parts.join(", ")}` };
}
